'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';

import { formatPrice } from '@/lib/utils/formatPrice';
import { getTaxLineLabel, isNoTaxCountry, isTaxIncluded } from '@/lib/tax';
import styles from './TaxLine.module.scss';

interface TaxLineProps {
  countryCode: string;
  amount: number;
  currency: string;
  context: 'cart' | 'checkout' | 'confirmation';
  locale?: string;
  loading?: boolean;
  loadingElement?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const TaxLine = ({
  countryCode,
  amount,
  currency,
  context,
  locale,
  loading,
  loadingElement,
  className,
  'data-testid': testId,
}: TaxLineProps) => {
  const t = useTranslations('price');

  if (isNoTaxCountry(countryCode)) return null;
  if (context === 'cart' && !isTaxIncluded(countryCode)) return null;

  const labelKey = getTaxLineLabel(countryCode);
  if (!labelKey) return null;

  const label = t(`tax.${labelKey}`);
  const formattedAmount = formatPrice(amount, currency, locale);

  const content = (
    <>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{loading ? (loadingElement ?? null) : formattedAmount}</span>
    </>
  );

  if (!className && !testId) return content;

  return (
    <div className={className} data-testid={testId}>
      {content}
    </div>
  );
};
